import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocale } from '../i18n/Locale.js';
import { OutfitWalker } from './OutfitWalker.js';
import './about-page.css';
import { AuthLogo } from './AuthShell.js';
import { afterResource } from './KazbiaLogo.js';
// The loot strip's item icons need the game's item data (~12 MB of module): loaded with the strip,
// so the login / about pages never wait for that package (owner 24/10).
const ItemArt = lazy(() => import('./ItemArt.js').then((m) => ({ default: m.ItemArt })));

/** Support channel for account problems (password recovery while e-mail is off). */
export const SUPPORT_DISCORD = 'https://discord.com/invite/HhzN8dTJFg';

/* ── "Continuar com o Google" ────────────────────────────────────────────────
   Same look as Google's own rendered button (theme filled_black, size large,
   rectangular, logo centred): #202124 plate, white logo tile, Google Sans 500.
   Visual only for now. To wire OAuth later, pass an onClick that starts the
   Google Identity Services flow (or swap this for the GIS iframe button with
   the same width) and post the credential to a new /api/google route. */
export function GoogleButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className="hz-google" onClick={onClick} disabled={disabled} data-testid="google-signin">
      <span className="hz-google-inner">
        <span className="hz-google-logo" aria-hidden>
          <svg viewBox="0 0 48 48" width="18" height="18">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
        </span>
        <span className="hz-google-label">{label}</span>
      </span>
    </button>
  );
}

/* ── Rules ──────────────────────────────────────────────────────────────── */

interface Rule {
  title: string;
  body: string[];
}

const RULES: Record<'pt' | 'en', { kicker: string; title: string; rules: Rule[]; close: string; accept: string }> = {
  pt: {
    kicker: 'Antes de jogar',
    title: 'Regras do Kazbia Online',
    close: 'Fechar',
    accept: 'Aceito',
    rules: [
      {
        title: 'Até 4 contas por pessoa',
        body: [
          'Você pode jogar com até 4 contas, o suficiente para formar uma full party sozinho. Usar mais de 4 é proibido.',
          'Tentativas de passar desse limite são sinalizadas pelos sistemas de proteção do jogo, analisadas pela equipe e podem levar a banimento.',
        ],
      },
      {
        title: 'Nada de automação externa',
        body: [
          'O Kazbia Online já caça por você: combate automático, rotas de hunt e progressão idle são o jogo. Bots, macros, clientes modificados ou qualquer outra coisa que jogue no seu lugar além do que o jogo oferece são proibidos, assim como divulgar ou incentivar o uso deles.',
          'Ferramentas de apoio que nunca agem por você continuam permitidas: medidores de DPS, ferramentas para abrir até 4 contas no mesmo navegador e recursos de organização ou visualização.',
        ],
      },
      {
        title: 'Nada de trapaça ou abuso de bugs',
        body: [
          'Qualquer forma de trapaça, assim como o uso de bugs ou falhas do jogo para obter vantagem, é contra as regras.',
          'Bugs devem ser reportados no nosso Discord. Explorar um bug pode resultar na remoção de tudo o que foi obtido com ele e em banimento.',
        ],
      },
      {
        title: 'Respeito no jogo e nos canais oficiais',
        body: [
          'Ofensas, ataques pessoais, desrespeito com jogadores ou com a equipe, racismo, discriminação, discurso de ódio, assédio, ameaças e comportamento tóxico proposital não são tolerados no jogo, no Discord ou em qualquer outro canal oficial.',
          'Isso pode ser punido sem aviso prévio, até com banimento permanente. Discordar e dar feedback é bem-vindo, sempre com respeito.',
        ],
      },
    ],
  },
  en: {
    kicker: 'Before you play',
    title: 'Kazbia Online rules',
    close: 'Close',
    accept: 'I accept',
    rules: [
      {
        title: 'Up to 4 accounts per person',
        body: [
          'You may play with up to 4 accounts, enough to form a full party on your own. Using more than 4 is forbidden.',
          "Attempts to go past this limit are flagged by the game's protection systems, reviewed by the team, and can lead to a ban.",
        ],
      },
      {
        title: 'No outside automation',
        body: [
          'Kazbia Online already hunts for you: automated combat, hunt routes and idle progression are the game. Bots, macros, modified clients or anything else that plays in your place beyond what the game offers are forbidden, and so is advertising or encouraging them.',
          'Helper tools that never act for you stay allowed: DPS meters, tools to open up to 4 accounts in the same browser, and organisation or display aids.',
        ],
      },
      {
        title: 'No cheating or bug abuse',
        body: [
          "Any form of cheating, and the use of bugs or flaws in the game to gain an advantage, is against the rules.",
          'Bugs must be reported on our Discord. Exploiting a bug may result in the removal of anything gained from it and in a ban.',
        ],
      },
      {
        title: 'Respect in the game and on official channels',
        body: [
          'Insults, personal attacks, disrespect towards players or the team, racism, discrimination, hate speech, harassment, threats and deliberately toxic behaviour are not tolerated in the game, on Discord or on any other official channel.',
          'They can be punished without a prior warning, up to a permanent ban. Disagreeing and giving feedback is welcome, as long as it is respectful.',
        ],
      },
    ],
  },
};

export function RulesModal({ onClose, onAccept }: { onClose: () => void; onAccept: () => void }) {
  const { locale } = useLocale();
  const copy = RULES[locale === 'en' ? 'en' : 'pt'];
  const acceptRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    acceptRef.current?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className="hzj-backdrop" aria-hidden onClick={onClose} />
      <section className="hzw hzj hz-rules" role="dialog" aria-modal="true" aria-labelledby="hz-rules-title" data-testid="rules-modal">
        <div className="hzj-body hz-rules-body">
          <span className="hz-rules-kicker">{copy.kicker}</span>
          <h2 className="hz-rules-title" id="hz-rules-title">{copy.title}</h2>
          <ol className="hz-rules-list">
            {copy.rules.map((rule) => (
              <li key={rule.title}>
                <h3>{rule.title}</h3>
                {rule.body.map((line) => <p key={line}>{line}</p>)}
              </li>
            ))}
          </ol>
        </div>
        <div className="hzj-actions hzj-actions--split hz-rules-actions">
          <button type="button" className="hzj-btn" onClick={onClose}>{copy.close}</button>
          <button ref={acceptRef} type="button" className="hzj-btn hzj-btn--main" onClick={onAccept} data-testid="rules-accept">{copy.accept}</button>
        </div>
      </section>
    </>
  );
}

/* ── "Sobre o jogo" (reference: the logged-out landing page) ───────────────── */

const ABOUT = {
  pt: {
    login: 'Entrar',
    play: 'Jogar agora',
    eyebrow: 'MMORPG no navegador · sem download',
    title: ['Um mundo clássico,', 'caçando por você'],
    sub: 'Os monstros, itens e fórmulas do Tibia que você conhece, num MMO idle: escolha a caçada, monte suas regras de poção e magia e o personagem caça sozinho — com a aba aberta ou fechada, no computador ou no celular.',
    create: 'Criar conta — é grátis',
    already: 'Já jogo',
    features: [
      { glyph: '🌐', title: 'Sem download', text: 'Sem launcher, sem cliente, sem instalação. O mundo carrega na página — crie a conta, dê nome a um personagem e você já está em Thais.' },
      { glyph: '⏳', title: 'Caça até offline', text: 'Feche a aba e a caçada continua. Quando você volta, o servidor já calculou cada minuto — experiência, loot e suprimentos gastos — e entrega o resumo.' },
      { glyph: '⚔️', title: 'Regras, não cliques', text: 'A barra de ações conduz a luta: poções na vida certa, magias em rotação, alvo e distância. Você decide a estratégia — hunt, equipamento, imbuements — e o personagem faz os cliques.' },
    ],
    lootEyebrow: 'Loot e progressão',
    lootTitle: 'O loot cai enquanto você descansa',
    lootSub: 'Bestiary, prey, imbuements, forja e bosses — e tudo o que cai passa pela sua mochila, pelo seu depot e pelo mercado entre jogadores.',
    finalTitle: 'Entre no mundo',
    finalSub: 'Criar a conta leva um minuto: crie, dê nome a um personagem e comece sua primeira caçada — tudo no navegador.',
    foot: 'Funciona melhor em um navegador atual, no computador ou no celular.',
  },
  en: {
    login: 'Sign in',
    play: 'Play now',
    eyebrow: 'Browser MMORPG · no download',
    title: ['A classic world,', 'hunting for you'],
    sub: 'The Tibia monsters, items and formulas you know, as an idle MMO: pick a hunt, set your potion and spell rules and your character hunts on its own — with the tab open or closed, on desktop or phone.',
    create: 'Create account — it\'s free',
    already: 'I already play',
    features: [
      { glyph: '🌐', title: 'No download', text: 'No launcher, no client, no install. The world loads in the page — sign up, name a character and you are in Thais.' },
      { glyph: '⏳', title: 'Hunts even offline', text: 'Close the tab and the hunt goes on. When you are back the server has worked out every minute — experience, loot and supplies used — and hands you the summary.' },
      { glyph: '⚔️', title: 'Rules, not clicks', text: 'The action bar runs the fight: potions at the right health, spells in rotation, target and distance. You make the calls — hunt, gear, imbuements — and your character does the clicking.' },
    ],
    lootEyebrow: 'Loot and progression',
    lootTitle: 'Loot drops while you rest',
    lootSub: 'Bestiary, prey, imbuements, the forge and bosses — and everything that drops goes through your backpack, your depot and the player market.',
    finalTitle: 'Enter the world',
    finalSub: 'Signing up takes a minute: create the account, name a character and start your first hunt — all in the browser.',
    foot: 'Works best in a current browser, on desktop or phone.',
  },
};

/**
 * Reference landing: the vocations walk on 128 px pixelated canvases (outfit
 * renderer, ~67 ms a frame) — and we have the Monk too.
 */
/** Keep the looks in step with ABOUT_LOOKS in tools/extractor/build_about_atlas.py (their small atlas). */
const ABOUT_OUTFITS: Array<{ look: number; name: string; colors: { head: number; body: number; legs: number; feet: number } }> = [
  { look: 131, name: 'Knight', colors: { head: 114, body: 120, legs: 114, feet: 115 } },
  { look: 137, name: 'Paladin', colors: { head: 78, body: 68, legs: 58, feet: 76 } },
  { look: 130, name: 'Sorcerer', colors: { head: 95, body: 94, legs: 94, feet: 115 } },
  { look: 144, name: 'Druid', colors: { head: 39, body: 57, legs: 76, feet: 95 } },
  { look: 1824, name: 'Monk', colors: { head: 95, body: 113, legs: 39, feet: 115 } },
];

/** Reference loot strip: 6 items on 64 px pixelated cells (the animated ones shimmer). */
const ABOUT_LOOT = [3366, 3281, 3420, 3079, 3364, 3043];

/**
 * Reference landing (app-vocation-outfit → outfit renderer ft(outfitId, canvas, colors) with its
 * defaults: direction 2, speed 1): the whole walking cycle (every phase of the moving group) in 540 ms,
 * ~67 ms a pose. 0.25 here made them walk four times too slow (owner 23/10 #25).
 */
const ABOUT_WALK_SPEED = 1;
function AboutOutfit({ look, colors, direction }: { look: number; colors: { head: number; body: number; legs: number; feet: number }; direction: number }) {
  return <OutfitWalker lookType={look} colors={colors} direction={direction} size={128} speed={ABOUT_WALK_SPEED} atlas="about-outfits" className="hz-landing-walker" style={{ width: undefined, height: undefined }} />;
}
export function AboutPage({ langSwitch, onLogin, onRegister }: { langSwitch: ReactNode; onLogin: () => void; onRegister: () => void }) {
  const { locale } = useLocale();
  const c = ABOUT[locale === 'en' ? 'en' : 'pt'];
  // The logo waits for the walking outfits' small atlas and their first frame (at most 0.8 s):
  // measured at 10 Mbps it otherwise pushed the first outfit from ~0.87 s to ~0.97 s.
  const [logoAfter] = useState(() => afterResource('about-outfits-0', 800, 120));
  // The loot strip's item atlas (~1.6 MB) waits until the strip is near the screen, so the walking
  // vocations at the top get the connection first (owner 23/10 #25: they took long to appear).
  const lootRef = useRef<HTMLDivElement>(null);
  const [lootNear, setLootNear] = useState(false);
  useEffect(() => {
    const el = lootRef.current;
    if (!el || lootNear) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setLootNear(true); return undefined; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setLootNear(true);
    }, { rootMargin: '300px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [lootNear]);
  const link = (href: string, handler: () => void, className: string, label: string) => (
    <a href={href} className={className} onClick={(event) => { event.preventDefault(); handler(); }}>{label}</a>
  );
  return (
    <div className="hz-landing" data-view="about">
      <header className="hz-landing-top">
        <span className="hz-landing-brand">KAZBIA <b>ONLINE</b></span>
        <nav className="hz-landing-nav">
          {langSwitch}
          {link('/login', onLogin, 'hz-landing-btn ghost', c.login)}
          {link('/register', onRegister, 'hz-landing-btn primary', c.play)}
        </nav>
      </header>
      <section className="hz-landing-hero">
        <div className="hz-landing-logo"><AuthLogo variant="hero" after={logoAfter} /></div>
        <p className="hz-landing-eyebrow">{c.eyebrow}</p>
        <h1 className="hz-landing-title">{c.title[0]}<br />{c.title[1]}</h1>
        <p className="hz-landing-sub">{c.sub}</p>
        <div className="hz-landing-cta">
          {link('/register', onRegister, 'hz-landing-btn primary big', c.create)}
          {link('/login', onLogin, 'hz-landing-btn ghost big', c.already)}
        </div>
        <div className="hz-landing-outfits">
          {ABOUT_OUTFITS.map((outfit) => (
            <figure key={outfit.name} className="hz-landing-outfit">
              <AboutOutfit look={outfit.look} colors={outfit.colors} direction={2} />
              <figcaption>{outfit.name}</figcaption>
            </figure>
          ))}
        </div>
      </section>
      <section className="hz-landing-features">
        {c.features.map((feature) => (
          <article key={feature.title}>
            <span className="hz-landing-glyph" aria-hidden>{feature.glyph}</span>
            <h3>{feature.title}</h3>
            <p>{feature.text}</p>
          </article>
        ))}
      </section>
      <section className="hz-landing-loot">
        <p className="hz-landing-eyebrow gold">{c.lootEyebrow}</p>
        <h2>{c.lootTitle}</h2>
        <p className="hz-landing-sub">{c.lootSub}</p>
        <div className="hz-landing-loot-grid" ref={lootRef}>
          {ABOUT_LOOT.map((id) => (
            <span key={id} className="hz-landing-loot-cell">{lootNear ? <Suspense fallback={null}><ItemArt id={id} count={id === 3043 ? 25 : 1} /></Suspense> : null}</span>
          ))}
        </div>
      </section>
      <section className="hz-landing-final">
        <h2>{c.finalTitle}</h2>
        <p className="hz-landing-sub">{c.finalSub}</p>
        {link('/register', onRegister, 'hz-landing-btn primary big', c.play)}
      </section>
      <footer className="hz-landing-foot">
        {/* Reference footer: only the name and the browser note — no community link. */}
        <span>Kazbia Online</span>
        <span>{c.foot}</span>
      </footer>
    </div>
  );
}
